import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Checkbox } from 'ui-components/components/checkbox';

import { emptyArray } from 'ui-components/default-props/empty';
import { OptionFormTypes } from '../../../../services/filtering/filter-form-render-service/option-form-types';
import Global from '../../../../store/global-selectors';
import { messages } from '../../../../components/customers-grid/columns/messages';
import DataDrivenLogo from '../../../../components/data-driven-logo';
import getDisplayInformation from '../../../../components/data-driven-logo/get-display-information';
import getMarketplaceDisplayName from '../../../../components/data-driven-logo/get-marketplace-display-name';
import style from '../common-styles.module.less';
import { noop } from 'ui-components/default-props/noop';

const isMarketplaceSelected = (selectedMarketplaces, marketplace) => {
  const marketplaceIndex = selectedMarketplaces.findIndex(
    (selectedMarketplace) => selectedMarketplace.marketplaceId === marketplace.marketplaceId,
  );
  return marketplaceIndex !== -1;
};

const MarketplaceDropdownOptions = ({ criteriaInformations, existingFiltersHelpers }) => {
  const sellerStores = useSelector(Global.selectSellerStores);
  const sellerStoresMarketplaceIds = useMemo(
    () =>
      sellerStores.reduce(
        (acc, store) => ({
          ...acc,
          [store.marketplaceId]: true,
        }),
        {},
      ),
    [sellerStores],
  );
  const { onApplyFilter, onRemoveFilter, existingFilters } = existingFiltersHelpers;
  const { selectedFilterBy, criterionArgumentOptions } = criteriaInformations;
  const availableMarketplaces = criterionArgumentOptions || emptyArray;
  const selectedMarketplaces = existingFilters.map((filter) => filter.criterionMeta);
  const sellerMarketplaces = useMemo(
    () => availableMarketplaces?.filter((marketplace) => sellerStoresMarketplaceIds[marketplace.marketplaceId]),
    [availableMarketplaces, sellerStoresMarketplaceIds],
  );

  const onMarketplaceToggle = (marketplace) => {
    if (!isMarketplaceSelected(selectedMarketplaces, marketplace)) {
      onApplyFilter(selectedFilterBy, undefined, { value: marketplace.marketplaceId }, marketplace);
      return;
    }
    const existingFilter = existingFilters.find((filter) => filter.criterionArgumentValue === marketplace.marketplaceId);
    onRemoveFilter(existingFilter);
  };

  if (!availableMarketplaces || availableMarketplaces.length === 0) {
    return null;
  }

  return (
    <>
      {sellerMarketplaces.map((marketplace) => {
        if (!marketplace.marketplaceId) {
          return null;
        }
        const displayInformation = getDisplayInformation({
          marketplaceId: marketplace.marketplaceId,
        });
        const displayName = getMarketplaceDisplayName(marketplace.marketplaceId);
        return (
          <DropdownMenuItem
            key={marketplace.marketplaceId}
            className={style['checklist-row']}
            onClick={() => onMarketplaceToggle(marketplace)}
          >
            <Checkbox checked={isMarketplaceSelected(selectedMarketplaces, marketplace)} onChange={noop} />
            <div className={style['normal-content']}>
              {displayInformation && (
                <DataDrivenLogo
                  className={style['third-party-logo']}
                  logoType='icon'
                  marketplaceId={Number.parseInt(marketplace.marketplaceId, 10)}
                />
              )}
              <div className={style.message}>
                <FormattedMessage {...displayName} />
              </div>
            </div>
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

MarketplaceDropdownOptions.propTypes = {
  criteriaInformations: PropTypes.shape({
    selectedFilterBy: PropTypes.object.isRequired,
    criterionArgumentOptions: PropTypes.array,
  }).isRequired,
  existingFiltersHelpers: PropTypes.shape({
    onApplyFilter: PropTypes.func.isRequired,
    onRemoveFilter: PropTypes.func.isRequired,
    existingFilters: PropTypes.array.isRequired,
  }).isRequired,
};

export const Marketplace = {
  id: 'marketplaceUsernames',
  displayName: <FormattedMessage {...messages.marketplaceUsernames} />,
  type: OptionFormTypes.TYPE_CHECKLIST,
  filterValue: 'FilterToMarketPlaces',
  expectsArgumentAsArray: true,
  allowsMultipleSelection: true,

  render: (criteriaInformations, existingFiltersHelpers) => (
    <MarketplaceDropdownOptions criteriaInformations={criteriaInformations} existingFiltersHelpers={existingFiltersHelpers} />
  ),

  shouldHideActionButtons: true,
};
